import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, RotateCcw, ArrowRight } from "lucide-react";
import { useOnboarding } from "@/context/OnboardingContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

export default function OnboardingChecklist({ compact = false, onNavigate }) {
  const { status, reset } = useOnboarding();
  if (!status) return <div className="text-sm text-slate-500 p-4">Loading checklist…</div>;
  const { items, completed_count, total, progress_pct } = status;

  // Group items
  const groups = {};
  items.forEach((it) => {
    (groups[it.group] = groups[it.group] || []).push(it);
  });

  return (
    <div className="space-y-4" data-testid="onboarding-checklist">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Onboarding progress</div>
          <div className="text-lg font-heading font-semibold text-slate-900 tabular-nums" data-testid="onboarding-progress-text">
            {completed_count} of {total} completed · {progress_pct}%
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={reset} className="shrink-0 h-8" data-testid="onboarding-reset">
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
        </Button>
      </div>

      <Progress value={progress_pct} className="h-2" data-testid="onboarding-progress-bar" />

      <div className={compact ? "grid grid-cols-1 gap-3" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
        {Object.entries(groups).map(([group, list]) => (
          <div key={group} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`onboarding-group-${group.toLowerCase().replace(/\s+/g,'-')}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{group}</div>
            <ul className="space-y-1.5">
              {list.map((it) => (
                <li key={it.key} className="flex items-start gap-2 group" data-testid={`onboarding-item-${it.key}`}>
                  {it.completed ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  ) : (
                    <Circle className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm ${it.completed ? "text-slate-500 line-through" : "text-slate-900"}`}>
                      {it.label}
                    </div>
                  </div>
                  {!it.completed && (
                    <Link
                      to={it.route}
                      onClick={onNavigate}
                      className="opacity-0 group-hover:opacity-100 text-[11px] uppercase tracking-widest text-sky-700 hover:text-sky-900 flex items-center gap-1 transition-opacity"
                      data-testid={`onboarding-go-${it.key}`}
                    >
                      Go <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
